import React, { useState, useEffect, useRef, useCallback } from "react";
import { DocumentArrowDownIcon, PhotoIcon } from "@heroicons/react/24/outline";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  format,
  subDays,
  startOfDay,
  endOfDay,
  startOfMonth,
  endOfMonth,
} from "date-fns";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { apiService } from "../../services/api";
import { Branch, Machine } from "../../types";
import LoadingSpinner from "../../components/common/LoadingSpinner";

interface ReportFilters {
  period: string;
  startDate: Date;
  endDate: Date;
  branchId: string;
  machineId: string;
  softwareFilter: string; // Novo filtro para software
}

const COLORS = [
  "#3B82F6",
  "#10B981",
  "#F59E0B",
  "#EF4444",
  "#8B5CF6",
  "#EC4899",
  "#06B6D4",
  "#84CC16",
  "#F97316",
  "#6366F1",
];

// Função para obter cor baseada no nome do OS
const getOSColor = (osName: string): string => {
  const name = osName.toLowerCase();
  if (name.includes("windows")) {
    return "#3B82F6"; // Azul para Windows
  } else if (
    name.includes("linux") ||
    name.includes("ubuntu") ||
    name.includes("debian") ||
    name.includes("centos") ||
    name.includes("fedora")
  ) {
    return "#F59E0B"; // Amarelo/Laranja para Linux
  }
  // Cores padrão para outros sistemas
  return COLORS[Math.floor(Math.random() * COLORS.length)];
};

// Função para obter cor baseada no navegador
const getBrowserColor = (browserName: string): string => {
  const name = browserName.toLowerCase();
  if (name.includes("chrome")) return "#F59E0B"; // Amarelo
  if (name.includes("firefox")) return "#F97316"; // Laranja
  if (name.includes("edge")) return "#3B82F6"; // Azul
  if (name.includes("opera gx")) return "#B91C1C"; // Vermelho Escuro (GX)
  if (name.includes("opera")) return "#EF4444"; // Vermelho
  if (name.includes("safari")) return "#0EA5E9"; // Azul Claro
  return COLORS[Math.floor(Math.random() * COLORS.length)];
};

// Função para obter ícone do navegador
const getBrowserIcon = (browserName: string): string => {
  const name = browserName.toLowerCase();
  if (name.includes("chrome")) return "chrome.png";
  if (name.includes("firefox")) return "firefox.png";
  if (name.includes("edge")) return "edge.png";
  if (name.includes("opera gx")) return "opera_gx.png";
  if (name.includes("opera")) return "opera.png";
  if (name.includes("safari")) return "safari.png";
  if (name.includes("brave")) return "brave.png";
  if (name.includes("vivaldi")) return "vivaldi.png";
  return "default.png";
};

// Função centralizada para filtrar impressoras que não devem aparecer no relatório
const shouldExcludePrinter = (printerName: string): boolean => {
  if (!printerName) return true;

  const name = printerName.toLowerCase();

  // Lista de termos que indicam impressoras virtuais ou softwares de impressão
  const excludedTerms = [
    "anydesk",
    "fax",
    "microsoft",
    "onenote",
    "pdf",
    "xps",
    "send to",
    "snagit",
    "cute pdf",
    "adobe pdf",
    "print to pdf",
    "microsoft print to pdf",
    "microsoft xps document writer",
    "foxit",
    "nitro",
    "primo pdf",
    "do pdf",
    "bullzip",
    "virtual",
    "virtual printer",
    "printer driver",
    "printer port",
    "redirected",
    "redirect",
  ];

  return excludedTerms.some((term) => name.includes(term));
};

export default function ReportsPage() {
  const [filters, setFilters] = useState<ReportFilters>({
    period: "today",
    startDate: startOfDay(new Date()),
    endDate: endOfDay(new Date()),
    branchId: "",
    machineId: "",
    softwareFilter: "", // Novo campo
  });

  const [branches, setBranches] = useState<Branch[]>([]);
  const [machines, setMachines] = useState<Machine[]>([]);
  const [isExporting, setIsExporting] = useState(false);
  const [companySettings, setCompanySettings] = useState<any>(null);

  // Estados para controlar quais relatórios carregar
  const [loadedReports, setLoadedReports] = useState<{
    productivity: boolean;
    webNavigation: boolean;
    inventory: boolean;
    system: boolean;
    offHoursUsage: boolean;
  }>({
    productivity: false,
    webNavigation: false,
    inventory: false,
    system: false,
    offHoursUsage: false,
  });

  // Estados de loading por tipo de relatório
  const [loadingReports, setLoadingReports] = useState<{
    productivity: boolean;
    webNavigation: boolean;
    inventory: boolean;
    system: boolean;
    offHoursUsage: boolean;
  }>({
    productivity: false,
    webNavigation: false,
    inventory: false,
    system: false,
    offHoursUsage: false,
  });

  // Report data
  const [productivityData, setProductivityData] = useState<any>(null);
  const [webNavigationData, setWebNavigationData] = useState<any>(null);
  const [inventoryData, setInventoryData] = useState<any>(null);
  const [systemData, setSystemData] = useState<any>(null);
  const [offHoursUsageData, setOffHoursUsageData] = useState<any>(null);

  const productivityRef = useRef<HTMLDivElement>(null);
  const webNavigationRef = useRef<HTMLDivElement>(null);
  const inventoryRef = useRef<HTMLDivElement>(null);
  const systemRef = useRef<HTMLDivElement>(null);
  const offHoursUsageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadBranches();
    loadMachines();
    loadCompanySettings();
  }, []);

  const loadCompanySettings = async () => {
    try {
      const response = await apiService.getCompanySettings();
      if (response.success && response.data) {
        setCompanySettings(response.data);
      }
    } catch (error) {
      console.error("Error loading company settings:", error);
    }
  };

  const loadBranches = async () => {
    try {
      const response = await apiService.getBranches();
      if (response.success) {
        setBranches(response.data || []);
      }
    } catch (error) {
      console.error("Error loading branches:", error);
    }
  };

  const loadMachines = async (branchId?: string) => {
    try {
      const params: any = {};
      if (branchId) {
        params.branch_id = branchId;
      }
      const response = await apiService.getMachines(params);
      setMachines(response.data || []);
    } catch (error) {
      console.error("Error loading machines:", error);
    }
  };

  // Carregar relatório de produtividade
  const loadProductivityReport = useCallback(async () => {
    setLoadingReports((prev) => ({ ...prev, productivity: true }));
    try {
      const params: any = {
        start_date: filters.startDate.toISOString(),
        end_date: filters.endDate.toISOString(),
      };

      if (filters.branchId) {
        params.branch_id = filters.branchId;
      }

      if (filters.machineId) {
        params.machine_id = filters.machineId;
      }

      // Adicionar filtro de software se especificado
      if (filters.softwareFilter) {
        params.software_filter = filters.softwareFilter;
      }

      const response = await apiService.getProductivityReport(params);
      if (response.success) {
        setProductivityData(response.data);
        setLoadedReports((prev) => ({ ...prev, productivity: true }));
      }
    } catch (error) {
      console.error("Error loading productivity report:", error);
    } finally {
      setLoadingReports((prev) => ({ ...prev, productivity: false }));
    }
  }, [filters]);

  // Carregar relatório de navegação web
  const loadWebNavigationReport = useCallback(async () => {
    setLoadingReports((prev) => ({ ...prev, webNavigation: true }));
    try {
      const params: any = {
        start_date: filters.startDate.toISOString(),
        end_date: filters.endDate.toISOString(),
      };

      if (filters.branchId) {
        params.branch_id = filters.branchId;
      }

      if (filters.machineId) {
        params.machine_id = filters.machineId;
      }

      const response = await apiService.getWebNavigationReport(params);
      if (response.success) {
        setWebNavigationData(response.data);
        setLoadedReports((prev) => ({ ...prev, webNavigation: true }));
      }
    } catch (error) {
      console.error("Error loading web navigation report:", error);
    } finally {
      setLoadingReports((prev) => ({ ...prev, webNavigation: false }));
    }
  }, [filters]);

  // Carregar relatório de inventário
  const loadInventoryReport = useCallback(async () => {
    setLoadingReports((prev) => ({ ...prev, inventory: true }));
    try {
      const params: any = {};
      if (filters.branchId) {
        params.branch_id = filters.branchId;
      }
      if (filters.machineId) {
        params.machine_id = filters.machineId;
      }

      const response = await apiService.getInventoryReport(params);
      if (response.success) {
        setInventoryData(response.data);
        setLoadedReports((prev) => ({ ...prev, inventory: true }));
      }
    } catch (error) {
      console.error("Error loading inventory report:", error);
    } finally {
      setLoadingReports((prev) => ({ ...prev, inventory: false }));
    }
  }, [filters]);

  // Carregar relatório de sistema
  const loadSystemReport = useCallback(async () => {
    setLoadingReports((prev) => ({ ...prev, system: true }));
    try {
      const params: any = {
        start_date: filters.startDate.toISOString(),
        end_date: filters.endDate.toISOString(),
      };

      if (filters.branchId) {
        params.branch_id = filters.branchId;
      }

      if (filters.machineId) {
        params.machine_id = filters.machineId;
      }

      const response = await apiService.getSystemReport(params);
      if (response.success) {
        setSystemData(response.data);
        setLoadedReports((prev) => ({ ...prev, system: true }));
      }
    } catch (error) {
      console.error("Error loading system report:", error);
    } finally {
      setLoadingReports((prev) => ({ ...prev, system: false }));
    }
  }, [filters]);

  // Carregar relatório de uso fora do horário
  const loadOffHoursUsageReport = useCallback(async () => {
    setLoadingReports((prev) => ({ ...prev, offHoursUsage: true }));
    try {
      const params: any = {
        start_date: filters.startDate.toISOString(),
        end_date: filters.endDate.toISOString(),
      };

      if (filters.branchId) {
        params.branch_id = filters.branchId;
      }

      if (filters.machineId) {
        params.machine_id = filters.machineId;
      }

      const response = await apiService.getOffHoursUsageReport(params);
      if (response.success) {
        setOffHoursUsageData(response.data);
        setLoadedReports((prev) => ({ ...prev, offHoursUsage: true }));
      }
    } catch (error) {
      console.error("Error loading off-hours usage report:", error);
    } finally {
      setLoadingReports((prev) => ({ ...prev, offHoursUsage: false }));
    }
  }, [filters]);

  useEffect(() => {
    if (filters.branchId) {
      loadMachines(filters.branchId);
    } else {
      loadMachines();
    }
  }, [filters.branchId]);

  const handlePeriodChange = (period: string) => {
    let startDate: Date;
    let endDate: Date = new Date();

    switch (period) {
      case "today":
        startDate = startOfDay(new Date());
        endDate = endOfDay(new Date());
        break;
      case "yesterday":
        startDate = startOfDay(subDays(new Date(), 1));
        endDate = endOfDay(subDays(new Date(), 1));
        break;
      case "last7days":
        startDate = startOfDay(subDays(new Date(), 7));
        endDate = endOfDay(new Date());
        break;
      case "last30days":
        startDate = startOfDay(subDays(new Date(), 30));
        endDate = endOfDay(new Date());
        break;
      case "thisMonth":
        startDate = startOfMonth(new Date());
        endDate = endOfMonth(new Date());
        break;
      case "custom":
        startDate = filters.startDate;
        endDate = filters.endDate;
        break;
      default:
        startDate = startOfDay(new Date());
        endDate = endOfDay(new Date());
    }

    setFilters({
      ...filters,
      period,
      startDate,
      endDate,
    });

    // Resetar relatórios carregados quando filtros mudarem
    setLoadedReports({
      productivity: false,
      webNavigation: false,
      inventory: false,
      system: false,
      offHoursUsage: false,
    });
  };

  // Exportar PDF de um relatório específico
  const exportReportToPDF = async (
    reportType:
      | "productivity"
      | "webNavigation"
      | "inventory"
      | "system"
      | "offHoursUsage",
  ) => {
    let ref: React.RefObject<HTMLDivElement>;
    let reportName: string;
    let reportTitle: string;

    switch (reportType) {
      case "productivity":
        ref = productivityRef;
        reportName = "produtividade";
        reportTitle = "Relatório de Produtividade";
        break;
      case "webNavigation":
        ref = webNavigationRef;
        reportName = "navegacao_web";
        reportTitle = "Relatório de Navegação Web";
        break;
      case "inventory":
        ref = inventoryRef;
        reportName = "inventario";
        reportTitle = "Relatório de Inventário";
        break;
      case "system":
        ref = systemRef;
        reportName = "sistema";
        reportTitle = "Relatório de Sistema e Monitoramento";
        break;
      case "offHoursUsage":
        ref = offHoursUsageRef;
        reportName = "uso_fora_horario";
        reportTitle = "Relatório de Uso Fora do Horário";
        break;
      default:
        return;
    }

    if (!ref.current) {
      alert(`Por favor, carregue o relatório de ${reportName} primeiro.`);
      return;
    }

    setIsExporting(true);
    try {
      // Criar elemento temporário com cabeçalho
      const headerDiv = document.createElement("div");
      headerDiv.style.position = "absolute";
      headerDiv.style.left = "-9999px";
      headerDiv.style.width = ref.current.offsetWidth + "px";
      headerDiv.style.backgroundColor = "#ffffff";
      headerDiv.style.padding = "20px";
      headerDiv.style.borderBottom = "2px solid #e5e7eb";
      headerDiv.style.display = "flex";
      headerDiv.style.alignItems = "center";
      headerDiv.style.gap = "15px";

      // Ícone da aplicação (aumentado)
      const iconImg = document.createElement("img");
      iconImg.src = "/favicon.ico";
      iconImg.style.width = "80px";
      iconImg.style.height = "80px";
      iconImg.style.objectFit = "contain";

      // Container de informações
      const infoDiv = document.createElement("div");
      infoDiv.style.flex = "1";

      // Nome da aplicação
      const appName = document.createElement("div");
      appName.textContent = "Hub Machines";
      appName.style.fontSize = "18px";
      appName.style.fontWeight = "bold";
      appName.style.color = "#1f2937";
      appName.style.marginBottom = "4px";

      // Nome da empresa
      const companyName = document.createElement("div");
      companyName.textContent = companySettings?.name || "Empresa";
      companyName.style.fontSize = "14px";
      companyName.style.color = "#6b7280";
      companyName.style.marginBottom = "4px";

      // Nome do relatório
      const reportNameDiv = document.createElement("div");
      reportNameDiv.textContent = reportTitle;
      reportNameDiv.style.fontSize = "16px";
      reportNameDiv.style.color = "#3b82f6";
      reportNameDiv.style.fontWeight = "600";

      // Data de geração
      const dateDiv = document.createElement("div");
      dateDiv.textContent = `Gerado em: ${format(new Date(), "dd/MM/yyyy HH:mm")}`;
      dateDiv.style.fontSize = "12px";
      dateDiv.style.color = "#9ca3af";
      dateDiv.style.marginTop = "4px";

      // Informações de filtros
      const filtersDiv = document.createElement("div");
      filtersDiv.style.marginTop = "8px";
      filtersDiv.style.paddingTop = "8px";
      filtersDiv.style.borderTop = "1px solid #e5e7eb";

      const filtersTitle = document.createElement("div");
      filtersTitle.textContent = "Filtros Aplicados:";
      filtersTitle.style.fontSize = "12px";
      filtersTitle.style.fontWeight = "600";
      filtersTitle.style.color = "#374151";
      filtersTitle.style.marginBottom = "4px";

      const filtersList = document.createElement("div");
      filtersList.style.fontSize = "11px";
      filtersList.style.color = "#6b7280";
      filtersList.style.lineHeight = "1.5";

      const filterItems: string[] = [];
      if (filters.period) {
        filterItems.push(
          `Período: ${filters.period === "today" ? "Hoje" : filters.period === "week" ? "Última Semana" : filters.period === "month" ? "Último Mês" : "Personalizado"}`,
        );
      }
      if (filters.startDate && filters.endDate) {
        filterItems.push(
          `Data: ${format(filters.startDate, "dd/MM/yyyy")} - ${format(filters.endDate, "dd/MM/yyyy")}`,
        );
      }
      if (filters.branchId) {
        const branch = branches.find((b) => b.id === filters.branchId);
        if (branch) {
          filterItems.push(`Filial: ${branch.name}`);
        }
      }
      if (filters.machineId) {
        const machine = machines.find((m) => m.id === filters.machineId);
        if (machine) {
          filterItems.push(`Máquina: ${machine.name}`);
        }
      }
      if (filters.softwareFilter) {
        filterItems.push(`Software: ${filters.softwareFilter}`);
      }

      if (filterItems.length === 0) {
        filterItems.push("Nenhum filtro aplicado");
      }

      filtersList.textContent = filterItems.join(" | ");
      filtersDiv.appendChild(filtersTitle);
      filtersDiv.appendChild(filtersList);

      infoDiv.appendChild(appName);
      infoDiv.appendChild(companyName);
      infoDiv.appendChild(reportNameDiv);
      infoDiv.appendChild(dateDiv);
      infoDiv.appendChild(filtersDiv);

      headerDiv.appendChild(iconImg);
      headerDiv.appendChild(infoDiv);

      document.body.appendChild(headerDiv);

      // Aguardar carregamento do ícone
      await new Promise((resolve) => {
        if (iconImg.complete) {
          resolve(true);
        } else {
          iconImg.onload = () => resolve(true);
          iconImg.onerror = () => resolve(true); // Continuar mesmo se ícone falhar
        }
      });

      // Capturar cabeçalho
      const headerCanvas = await html2canvas(headerDiv, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
      });

      // Capturar conteúdo do relatório
      const contentCanvas = await html2canvas(ref.current, {
        scale: 2,
        useCORS: true,
        logging: false,
      });

      // Remover elemento temporário
      document.body.removeChild(headerDiv);

      // Criar PDF
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const margin = 10;
      const contentWidth = pdfWidth - margin * 2;

      // Adicionar cabeçalho
      const headerHeight =
        (headerCanvas.height * contentWidth) / headerCanvas.width;
      const headerImgData = headerCanvas.toDataURL("image/png");
      pdf.addImage(
        headerImgData,
        "PNG",
        margin,
        margin,
        contentWidth,
        headerHeight,
      );

      // Adicionar conteúdo
      const contentHeight =
        (contentCanvas.height * contentWidth) / contentCanvas.width;
      const contentImgData = contentCanvas.toDataURL("image/png");
      let contentPosition = margin + headerHeight + 5;
      let contentHeightLeft = contentHeight;

      // Primeira página
      const availableHeight =
        pdf.internal.pageSize.getHeight() - contentPosition - margin;
      const firstPageHeight = Math.min(contentHeightLeft, availableHeight);
      pdf.addImage(
        contentImgData,
        "PNG",
        margin,
        contentPosition,
        contentWidth,
        firstPageHeight,
      );
      contentHeightLeft -= firstPageHeight;

      // Páginas adicionais
      while (contentHeightLeft > 0) {
        pdf.addPage();
        const pageHeight = Math.min(
          contentHeightLeft,
          pdf.internal.pageSize.getHeight() - margin * 2,
        );
        const sourceY =
          (contentHeight - contentHeightLeft) *
          (contentCanvas.height / contentHeight);
        const sourceHeight =
          pageHeight * (contentCanvas.height / contentHeight);

        // Criar canvas temporário para a parte do conteúdo
        const tempCanvas = document.createElement("canvas");
        tempCanvas.width = contentCanvas.width;
        tempCanvas.height = sourceHeight;
        const tempCtx = tempCanvas.getContext("2d");
        if (tempCtx) {
          tempCtx.drawImage(
            contentCanvas,
            0,
            sourceY,
            contentCanvas.width,
            sourceHeight,
            0,
            0,
            contentCanvas.width,
            sourceHeight,
          );
          const tempImgData = tempCanvas.toDataURL("image/png");
          pdf.addImage(
            tempImgData,
            "PNG",
            margin,
            margin,
            contentWidth,
            pageHeight,
          );
        }

        contentHeightLeft -= pageHeight;
      }

      const fileName = `relatorio_${reportName}_${format(new Date(), "yyyy-MM-dd_HH-mm-ss")}.pdf`;
      pdf.save(fileName);
    } catch (error) {
      console.error("Error exporting to PDF:", error);
      alert("Erro ao exportar PDF. Tente novamente.");
    } finally {
      setIsExporting(false);
    }
  };

  // Exportar imagem de um relatório específico
  const exportReportToImage = async (
    reportType:
      | "productivity"
      | "webNavigation"
      | "inventory"
      | "system"
      | "offHoursUsage",
  ) => {
    let ref: React.RefObject<HTMLDivElement>;
    let reportName: string;
    let reportTitle: string;

    switch (reportType) {
      case "productivity":
        ref = productivityRef;
        reportName = "produtividade";
        reportTitle = "Relatório de Produtividade";
        break;
      case "webNavigation":
        ref = webNavigationRef;
        reportName = "navegacao_web";
        reportTitle = "Relatório de Navegação Web";
        break;
      case "inventory":
        ref = inventoryRef;
        reportName = "inventario";
        reportTitle = "Relatório de Inventário";
        break;
      case "system":
        ref = systemRef;
        reportName = "sistema";
        reportTitle = "Relatório de Sistema e Monitoramento";
        break;
      case "offHoursUsage":
        ref = offHoursUsageRef;
        reportName = "uso_fora_horario";
        reportTitle = "Relatório de Uso Fora do Horário";
        break;
      default:
        return;
    }

    if (!ref.current) {
      alert(`Por favor, carregue o relatório de ${reportName} primeiro.`);
      return;
    }

    setIsExporting(true);
    try {
      // Criar elemento temporário com cabeçalho
      const headerDiv = document.createElement("div");
      headerDiv.style.position = "absolute";
      headerDiv.style.left = "-9999px";
      headerDiv.style.width = ref.current.offsetWidth + "px";
      headerDiv.style.backgroundColor = "#ffffff";
      headerDiv.style.padding = "20px";
      headerDiv.style.borderBottom = "2px solid #e5e7eb";
      headerDiv.style.display = "flex";
      headerDiv.style.alignItems = "center";
      headerDiv.style.gap = "15px";

      // Ícone da aplicação (aumentado)
      const iconImg = document.createElement("img");
      iconImg.src = "/favicon.ico";
      iconImg.style.width = "80px";
      iconImg.style.height = "80px";
      iconImg.style.objectFit = "contain";

      // Container de informações
      const infoDiv = document.createElement("div");
      infoDiv.style.flex = "1";

      // Nome da aplicação
      const appName = document.createElement("div");
      appName.textContent = "Hub Machines";
      appName.style.fontSize = "18px";
      appName.style.fontWeight = "bold";
      appName.style.color = "#1f2937";
      appName.style.marginBottom = "4px";

      // Nome da empresa
      const companyName = document.createElement("div");
      companyName.textContent = companySettings?.name || "Empresa";
      companyName.style.fontSize = "14px";
      companyName.style.color = "#6b7280";
      companyName.style.marginBottom = "4px";

      // Nome do relatório
      const reportNameDiv = document.createElement("div");
      reportNameDiv.textContent = reportTitle;
      reportNameDiv.style.fontSize = "16px";
      reportNameDiv.style.color = "#3b82f6";
      reportNameDiv.style.fontWeight = "600";

      // Data de geração
      const dateDiv = document.createElement("div");
      dateDiv.textContent = `Gerado em: ${format(new Date(), "dd/MM/yyyy HH:mm")}`;
      dateDiv.style.fontSize = "12px";
      dateDiv.style.color = "#9ca3af";
      dateDiv.style.marginTop = "4px";

      // Informações de filtros
      const filtersDiv = document.createElement("div");
      filtersDiv.style.marginTop = "8px";
      filtersDiv.style.paddingTop = "8px";
      filtersDiv.style.borderTop = "1px solid #e5e7eb";

      const filtersTitle = document.createElement("div");
      filtersTitle.textContent = "Filtros Aplicados:";
      filtersTitle.style.fontSize = "12px";
      filtersTitle.style.fontWeight = "600";
      filtersTitle.style.color = "#374151";
      filtersTitle.style.marginBottom = "4px";

      const filtersList = document.createElement("div");
      filtersList.style.fontSize = "11px";
      filtersList.style.color = "#6b7280";
      filtersList.style.lineHeight = "1.5";

      const filterItems: string[] = [];
      if (filters.period) {
        filterItems.push(
          `Período: ${filters.period === "today" ? "Hoje" : filters.period === "week" ? "Última Semana" : filters.period === "month" ? "Último Mês" : "Personalizado"}`,
        );
      }
      if (filters.startDate && filters.endDate) {
        filterItems.push(
          `Data: ${format(filters.startDate, "dd/MM/yyyy")} - ${format(filters.endDate, "dd/MM/yyyy")}`,
        );
      }
      if (filters.branchId) {
        const branch = branches.find((b) => b.id === filters.branchId);
        if (branch) {
          filterItems.push(`Filial: ${branch.name}`);
        }
      }
      if (filters.machineId) {
        const machine = machines.find((m) => m.id === filters.machineId);
        if (machine) {
          filterItems.push(`Máquina: ${machine.name}`);
        }
      }
      if (filters.softwareFilter) {
        filterItems.push(`Software: ${filters.softwareFilter}`);
      }

      if (filterItems.length === 0) {
        filterItems.push("Nenhum filtro aplicado");
      }

      filtersList.textContent = filterItems.join(" | ");
      filtersDiv.appendChild(filtersTitle);
      filtersDiv.appendChild(filtersList);

      infoDiv.appendChild(appName);
      infoDiv.appendChild(companyName);
      infoDiv.appendChild(reportNameDiv);
      infoDiv.appendChild(dateDiv);
      infoDiv.appendChild(filtersDiv);

      headerDiv.appendChild(iconImg);
      headerDiv.appendChild(infoDiv);

      document.body.appendChild(headerDiv);

      // Aguardar carregamento do ícone
      await new Promise((resolve) => {
        if (iconImg.complete) {
          resolve(true);
        } else {
          iconImg.onload = () => resolve(true);
          iconImg.onerror = () => resolve(true); // Continuar mesmo se ícone falhar
        }
      });

      // Capturar cabeçalho
      const headerCanvas = await html2canvas(headerDiv, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
      });

      // Capturar conteúdo do relatório
      const contentCanvas = await html2canvas(ref.current, {
        scale: 2,
        useCORS: true,
        logging: false,
      });

      // Remover elemento temporário
      document.body.removeChild(headerDiv);

      // Combinar cabeçalho e conteúdo em um único canvas
      const combinedCanvas = document.createElement("canvas");
      combinedCanvas.width = Math.max(headerCanvas.width, contentCanvas.width);
      combinedCanvas.height = headerCanvas.height + contentCanvas.height;
      const ctx = combinedCanvas.getContext("2d");

      if (ctx) {
        // Fundo branco
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, combinedCanvas.width, combinedCanvas.height);

        // Desenhar cabeçalho
        ctx.drawImage(headerCanvas, 0, 0);

        // Desenhar conteúdo
        ctx.drawImage(contentCanvas, 0, headerCanvas.height);
      }

      const imgData = combinedCanvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.download = `relatorio_${reportName}_${format(new Date(), "yyyy-MM-dd_HH-mm-ss")}.png`;
      link.href = imgData;
      link.click();
    } catch (error) {
      console.error("Error exporting to image:", error);
      alert("Erro ao exportar imagem. Tente novamente.");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Relatórios
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Análise de produtividade, navegação web e inventário
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Period Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Período
            </label>
            <select
              value={filters.period}
              onChange={(e) => handlePeriodChange(e.target.value)}
              className="w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="today">Hoje</option>
              <option value="yesterday">Ontem</option>
              <option value="last7days">Últimos 7 dias</option>
              <option value="last30days">Últimos 30 dias</option>
              <option value="thisMonth">Mês Atual</option>
              <option value="custom">Personalizado</option>
            </select>
          </div>

          {/* Custom Date Range */}
          {filters.period === "custom" && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Data Inicial
                </label>
                <input
                  type="date"
                  value={format(filters.startDate, "yyyy-MM-dd")}
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      startDate: new Date(e.target.value),
                    })
                  }
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Data Final
                </label>
                <input
                  type="date"
                  value={format(filters.endDate, "yyyy-MM-dd")}
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      endDate: new Date(e.target.value),
                    })
                  }
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
            </>
          )}

          {/* Branch Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Filial
            </label>
            <select
              value={filters.branchId}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  branchId: e.target.value,
                  machineId: "",
                })
              }
              className="w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="">Todas as Filiais</option>
              {branches.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </select>
          </div>

          {/* Machine Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Máquina
            </label>
            <select
              value={filters.machineId}
              onChange={(e) =>
                setFilters({ ...filters, machineId: e.target.value })
              }
              className="w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="">Todas as Máquinas</option>
              {machines.map((machine) => (
                <option key={machine.id} value={machine.id}>
                  {machine.name}{" "}
                  {machine.user_name ? `(${machine.user_name})` : ""}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Reports Content */}
      <div className="space-y-6">
        {/* Productivity Report */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Produtividade
            </h2>
            <div className="flex space-x-2">
              {!loadedReports.productivity ? (
                <button
                  onClick={loadProductivityReport}
                  disabled={loadingReports.productivity}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {loadingReports.productivity ? (
                    <>
                      <LoadingSpinner size="sm" className="mr-2" />
                      Carregando...
                    </>
                  ) : (
                    "Carregar Relatório"
                  )}
                </button>
              ) : (
                <>
                  <button
                    onClick={() => exportReportToPDF("productivity")}
                    disabled={isExporting}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    <DocumentArrowDownIcon className="w-4 h-4 mr-1" />
                    PDF
                  </button>
                  <button
                    onClick={() => exportReportToImage("productivity")}
                    disabled={isExporting}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md shadow-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    <PhotoIcon className="w-4 h-4 mr-1" />
                    PNG
                  </button>
                </>
              )}
            </div>
          </div>

          {loadedReports.productivity && productivityData && (
            <div ref={productivityRef} className="space-y-6">
              {/* Filtro de Software para Ranking de Usuários */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Filtrar Ranking de Usuários por Software (opcional)
                </label>
                <input
                  type="text"
                  value={filters.softwareFilter}
                  onChange={(e) => {
                    setFilters({ ...filters, softwareFilter: e.target.value });
                    setLoadedReports((prev) => ({
                      ...prev,
                      productivity: false,
                    }));
                  }}
                  placeholder="Ex: chrome.exe, excel.exe, etc."
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
                {filters.softwareFilter && (
                  <button
                    onClick={loadProductivityReport}
                    disabled={loadingReports.productivity}
                    className="mt-2 inline-flex items-center px-3 py-1 text-sm font-medium rounded-md text-blue-600 hover:text-blue-700 disabled:opacity-50"
                  >
                    {loadingReports.productivity
                      ? "Aplicando..."
                      : "Aplicar Filtro"}
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Top 10 Software */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    Top 10 Softwares Mais Usados
                  </h3>
                  <ResponsiveContainer
                    width="100%"
                    height={400}
                    minWidth={0}
                    minHeight={0}
                  >
                    <BarChart
                      data={productivityData.top_software || []}
                      layout="vertical"
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        type="number"
                        tickFormatter={(value) =>
                          `${(value / 3600).toFixed(0)}h`
                        }
                      />
                      <YAxis
                        dataKey="name"
                        type="category"
                        width={150}
                        tick={{ fontSize: 12 }}
                      />
                      <Tooltip
                        formatter={(value: number) => [
                          `${(value / 3600).toFixed(2)} horas`,
                          "Tempo Total",
                        ]}
                      />
                      <Bar dataKey="total_seconds" fill="#3B82F6" />
                    </BarChart>
                  </ResponsiveContainer>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                    * Os valores exibidos representam o tempo total (em horas)
                    que cada software esteve em uso no período selecionado.
                  </p>
                </div>

                {/* Top Users */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    Ranking de Computadores Mais Ativos
                    {filters.softwareFilter && (
                      <span className="text-sm text-gray-500 dark:text-gray-400 ml-2">
                        (Filtrado por: {filters.softwareFilter})
                      </span>
                    )}
                  </h3>
                  <ResponsiveContainer
                    width="100%"
                    height={400}
                    minWidth={0}
                    minHeight={0}
                  >
                    <BarChart
                      data={productivityData.top_users || []}
                      layout="vertical"
                      margin={{
                        top: 5,
                        right: 30,
                        left: 0,
                        bottom: 5,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" />
                      <YAxis
                        dataKey="machine_name"
                        type="category"
                        width={200}
                        tick={{ fontSize: 12 }}
                      />
                      <Tooltip
                        formatter={(value: number) => [
                          `${value.toFixed(2)} horas`,
                          "Tempo Total",
                        ]}
                      />
                      <Bar
                        dataKey="total_hours"
                        fill="#10B981"
                        radius={[0, 4, 4, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                    * Os valores exibidos representam o tempo total (em horas)
                    que cada computador esteve com aplicações abertas no período
                    selecionado.
                  </p>
                </div>
              </div>

              {/* Least Active Machines */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  Computadores Menos Ativos (Top 10)
                </h3>
                <ResponsiveContainer
                  width="100%"
                  height={400}
                  minWidth={0}
                  minHeight={0}
                >
                  <BarChart
                    data={productivityData.least_active_users || []}
                    layout="vertical"
                    margin={{ left: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis
                      type="number"
                      tickFormatter={(value) => `${value.toFixed(0)}h`}
                    />
                    <YAxis
                      dataKey="machine_name"
                      type="category"
                      width={200}
                      tick={{ fontSize: 12 }}
                    />
                    <Tooltip
                      formatter={(value: number) => [
                        `${value.toFixed(2)} horas`,
                        "Tempo Total",
                      ]}
                    />
                    <Bar
                      dataKey="total_hours"
                      fill="#EF4444"
                      radius={[0, 4, 4, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                  * Os valores exibidos representam o tempo total (em horas) que
                  cada computador esteve com aplicações abertas no período
                  selecionado.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Web Navigation Report */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Navegação Web
            </h2>
            <div className="flex space-x-2">
              {!loadedReports.webNavigation ? (
                <button
                  onClick={loadWebNavigationReport}
                  disabled={loadingReports.webNavigation}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {loadingReports.webNavigation ? (
                    <>
                      <LoadingSpinner size="sm" className="mr-2" />
                      Carregando...
                    </>
                  ) : (
                    "Carregar Relatório"
                  )}
                </button>
              ) : (
                <>
                  <button
                    onClick={() => exportReportToPDF("webNavigation")}
                    disabled={isExporting}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    <DocumentArrowDownIcon className="w-4 h-4 mr-1" />
                    PDF
                  </button>
                  <button
                    onClick={() => exportReportToImage("webNavigation")}
                    disabled={isExporting}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md shadow-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    <PhotoIcon className="w-4 h-4 mr-1" />
                    PNG
                  </button>
                </>
              )}
            </div>
          </div>

          {loadedReports.webNavigation && webNavigationData && (
            <div ref={webNavigationRef} className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Top Domains */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    Domínios Mais Acessados
                  </h3>
                  <ResponsiveContainer
                    width="100%"
                    height={400}
                    minWidth={0}
                    minHeight={0}
                  >
                    <BarChart
                      data={webNavigationData.top_domains || []}
                      layout="vertical"
                      margin={{ left: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" />
                      <YAxis
                        dataKey="domain"
                        type="category"
                        width={200}
                        tick={{ fontSize: 12 }}
                      />
                      <Tooltip />
                      <Bar
                        dataKey="visit_count"
                        fill="#8B5CF6"
                        radius={[0, 4, 4, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                    * Os valores exibidos representam a quantidade de vezes que
                    cada domínio foi acessado no período selecionado.
                  </p>
                </div>

                {/* Browser Stats */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    Estatísticas de Navegadores
                  </h3>
                  <ResponsiveContainer
                    width="100%"
                    height={300}
                    minWidth={0}
                    minHeight={0}
                  >
                    <PieChart>
                      <Pie
                        data={webNavigationData?.browser_stats || []}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        fill="#8884d8"
                        paddingAngle={5}
                        dataKey="visit_count"
                        nameKey="browser"
                      >
                        {(webNavigationData?.browser_stats || []).map(
                          (entry: any, index: number) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={getBrowserColor(entry.browser)}
                            />
                          ),
                        )}
                      </Pie>
                      <Tooltip />
                      <Legend
                        layout="vertical"
                        verticalAlign="middle"
                        align="right"
                        content={({ payload }) => {
                          return (
                            <ul className="space-y-2">
                              {payload?.map((entry: any, index: number) => {
                                return (
                                  <li
                                    key={`item-${index}`}
                                    className="flex items-center text-sm text-gray-600 dark:text-gray-300"
                                  >
                                    <img
                                      src={`/assets/icons/browsers/${getBrowserIcon(entry.value)}`}
                                      alt={entry.value}
                                      className="w-4 h-4 mr-2"
                                      onError={(e) => {
                                        e.currentTarget.style.display = "none";
                                        // Note: Cannot easily show fallback SVG here without more complex React state code
                                      }}
                                    />
                                    <span
                                      className="w-3 h-3 rounded-full mr-2"
                                      style={{ backgroundColor: entry.color }}
                                    ></span>
                                    {entry.value}
                                  </li>
                                );
                              })}
                            </ul>
                          );
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                    * Os valores exibidos representam a quantidade de acessos
                    realizados por cada navegador no período selecionado.
                  </p>
                </div>
              </div>

              {/* Blocked Sites */}
              {webNavigationData.blocked_sites &&
                webNavigationData.blocked_sites.length > 0 && (
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                      Tentativas de Acesso a Sites Bloqueados
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              URL
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Tentativas
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                          {webNavigationData.blocked_sites.map(
                            (site: any, index: number) => (
                              <tr key={index}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                  {site.url}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                  {site.attempt_count}
                                </td>
                              </tr>
                            ),
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

              {/* Top Blocked Users */}
              {webNavigationData.top_blocked_users &&
                webNavigationData.top_blocked_users.length > 0 && (
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                      Usuários com Mais Bloqueios
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Usuário
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Tentativas de Acesso Bloqueado
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                          {webNavigationData.top_blocked_users.map(
                            (user: any, index: number) => (
                              <tr key={index}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                  {user.user_name}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                  {user.attempt_count}
                                </td>
                              </tr>
                            ),
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
            </div>
          )}
        </div>

        {/* Inventory Report */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Inventário
            </h2>
            <div className="flex space-x-2">
              {!loadedReports.inventory ? (
                <button
                  onClick={loadInventoryReport}
                  disabled={loadingReports.inventory}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {loadingReports.inventory ? (
                    <>
                      <LoadingSpinner size="sm" className="mr-2" />
                      Carregando...
                    </>
                  ) : (
                    "Carregar Relatório"
                  )}
                </button>
              ) : (
                <>
                  <button
                    onClick={() => exportReportToPDF("inventory")}
                    disabled={isExporting}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    <DocumentArrowDownIcon className="w-4 h-4 mr-1" />
                    PDF
                  </button>
                  <button
                    onClick={() => exportReportToImage("inventory")}
                    disabled={isExporting}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md shadow-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    <PhotoIcon className="w-4 h-4 mr-1" />
                    PNG
                  </button>
                </>
              )}
            </div>
          </div>

          {loadedReports.inventory && inventoryData && (
            <div ref={inventoryRef} className="space-y-6">
              {/* First Row: Printers (Full Width) */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  Impressoras Disponíveis
                </h3>
                {!inventoryData.printers_distribution ||
                inventoryData.printers_distribution.length === 0 ? (
                  <p className="text-gray-500 dark:text-gray-400">
                    Nenhuma impressora encontrada
                  </p>
                ) : (
                  (() => {
                    // Filtrar impressoras usando a função de blacklist
                    const filteredPrinters = (
                      inventoryData.printers_distribution || []
                    ).filter(
                      (printer: any) => !shouldExcludePrinter(printer.name),
                    );

                    if (filteredPrinters.length === 0) {
                      return (
                        <p className="text-gray-500 dark:text-gray-400">
                          Nenhuma impressora válida encontrada após filtragem
                        </p>
                      );
                    }

                    return (
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Gráfico Pizza à Esquerda */}
                        <div>
                          <ResponsiveContainer
                            width="100%"
                            height={400}
                            minWidth={0}
                            minHeight={0}
                          >
                            <PieChart>
                              <Pie
                                data={filteredPrinters}
                                dataKey="count"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                outerRadius={120}
                                label
                              >
                                {filteredPrinters.map(
                                  (entry: any, index: number) => (
                                    <Cell
                                      key={`cell-${index}`}
                                      fill={COLORS[index % COLORS.length]}
                                    />
                                  ),
                                )}
                              </Pie>
                              <Tooltip />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>

                        {/* Lista de Impressoras à Direita */}
                        <div>
                          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 max-h-[400px] overflow-y-auto">
                            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                              Legenda
                            </h4>
                            <div className="space-y-2">
                              {filteredPrinters.map(
                                (entry: any, index: number) => (
                                  <div
                                    key={index}
                                    className="flex items-center space-x-3 p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-600"
                                  >
                                    <div
                                      className="w-4 h-4 rounded-full"
                                      style={{
                                        backgroundColor:
                                          COLORS[index % COLORS.length],
                                      }}
                                    ></div>
                                    <div className="flex-1">
                                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                                        {entry.name}
                                      </div>
                                      <div className="text-xs text-gray-500 dark:text-gray-400">
                                        {entry.count}{" "}
                                        {entry.count === 1
                                          ? "impressora"
                                          : "impressoras"}
                                      </div>
                                    </div>
                                  </div>
                                ),
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })()
                )}
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                  * Os valores exibidos representam a quantidade de impressoras
                  de cada modelo disponíveis.
                </p>
              </div>

              {/* Second Row: OS Distribution | CPU Distribution */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* OS Distribution */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    Distribuição de Sistemas Operacionais
                  </h3>
                  <ResponsiveContainer
                    width="100%"
                    height={400}
                    minWidth={0}
                    minHeight={0}
                  >
                    <PieChart>
                      <Pie
                        data={inventoryData.os_distribution || []}
                        dataKey="count"
                        nameKey="os_name"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        label
                      >
                        {(inventoryData.os_distribution || []).map(
                          (entry: any, index: number) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={getOSColor(entry.os_name)}
                            />
                          ),
                        )}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                    * Os valores exibidos representam a quantidade de máquinas
                    que utilizam cada sistema operacional.
                  </p>
                </div>

                {/* CPU Distribution */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    Distribuição de Processadores
                  </h3>
                  <ResponsiveContainer
                    width="100%"
                    height={400}
                    minWidth={0}
                    minHeight={0}
                  >
                    <BarChart
                      data={inventoryData.cpu_distribution || []}
                      layout="vertical"
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis
                        dataKey="cpu"
                        type="category"
                        width={150}
                        tick={{ fontSize: 11 }}
                      />
                      <Tooltip />
                      <Bar dataKey="count" fill="#F59E0B" />
                    </BarChart>
                  </ResponsiveContainer>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                    * Os valores exibidos representam a quantidade de máquinas
                    que possuem cada modelo de processador.
                  </p>
                </div>
              </div>

              {/* Third Row: RAM Distribution | GPU Distribution */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* RAM Distribution */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    Distribuição de Memória RAM
                  </h3>
                  <ResponsiveContainer
                    width="100%"
                    height={400}
                    minWidth={0}
                    minHeight={0}
                  >
                    <BarChart
                      data={inventoryData.ram_distribution || []}
                      layout="vertical"
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="ram_range" type="category" width={100} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#10B981" />
                    </BarChart>
                  </ResponsiveContainer>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                    * Os valores exibidos representam a quantidade de máquinas
                    que possuem cada faixa de memória RAM.
                  </p>
                </div>

                {/* GPU Distribution */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    Distribuição de Placas de Vídeo (GPU)
                  </h3>
                  {!inventoryData.gpu_distribution ||
                  inventoryData.gpu_distribution.length === 0 ? (
                    <p className="text-gray-500 dark:text-gray-400">
                      Nenhum dado de GPU disponível
                    </p>
                  ) : (
                    <>
                      <ResponsiveContainer
                        width="100%"
                        height={400}
                        minWidth={0}
                        minHeight={0}
                      >
                        <BarChart
                          data={inventoryData.gpu_distribution || []}
                          layout="vertical"
                          margin={{
                            top: 5,
                            right: 30,
                            left: 0,
                            bottom: 5,
                          }}
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                            horizontal={false}
                          />
                          <XAxis type="number" />
                          <YAxis
                            dataKey="gpu"
                            type="category"
                            width={250}
                            tick={{ fontSize: 11 }}
                          />
                          <Tooltip />
                          <Bar
                            dataKey="count"
                            fill="#8B5CF6"
                            radius={[0, 4, 4, 0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                        * Os valores exibidos representam a quantidade de
                        máquinas que possuem cada modelo de placa de vídeo
                        (GPU).
                      </p>
                    </>
                  )}
                </div>
              </div>

              {/* Critical Alerts */}
              {inventoryData.critical_alerts &&
                inventoryData.critical_alerts.length > 0 && (
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                      Alertas Críticos
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Máquina
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Usuário
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Título
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Data
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                          {inventoryData.critical_alerts.map(
                            (alert: any, index: number) => (
                              <tr key={index}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                  {alert.machine_name}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                  {alert.user_name}
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                                  {alert.title}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                  {format(
                                    new Date(alert.created_at),
                                    "dd/MM/yyyy HH:mm",
                                  )}
                                </td>
                              </tr>
                            ),
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
            </div>
          )}
        </div>

        {/* System Report */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Sistema e Monitoramento
            </h2>
            <div className="flex space-x-2">
              {!loadedReports.system ? (
                <button
                  onClick={loadSystemReport}
                  disabled={loadingReports.system}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {loadingReports.system ? (
                    <>
                      <LoadingSpinner size="sm" className="mr-2" />
                      Carregando...
                    </>
                  ) : (
                    "Carregar Relatório"
                  )}
                </button>
              ) : (
                <>
                  <button
                    onClick={() => exportReportToPDF("system")}
                    disabled={isExporting}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    <DocumentArrowDownIcon className="w-4 h-4 mr-1" />
                    PDF
                  </button>
                  <button
                    onClick={() => exportReportToImage("system")}
                    disabled={isExporting}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md shadow-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    <PhotoIcon className="w-4 h-4 mr-1" />
                    PNG
                  </button>
                </>
              )}
            </div>
          </div>

          {loadedReports.system && systemData && (
            <div ref={systemRef} className="space-y-6">
              {/* Alerts by Branch */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  Número de Alertas Gerados por Filial
                </h3>
                {!systemData.alerts_by_branch ||
                systemData.alerts_by_branch.length === 0 ? (
                  <p className="text-gray-500 dark:text-gray-400">
                    Nenhum alerta gerado no período selecionado
                  </p>
                ) : (
                  <ResponsiveContainer
                    width="100%"
                    height={400}
                    minWidth={0}
                    minHeight={0}
                  >
                    <BarChart
                      data={systemData.alerts_by_branch.map((item: any) => ({
                        name: item.branch_name,
                        value: item.count,
                      }))}
                      layout="vertical"
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis type="number" stroke="#9CA3AF" />
                      <YAxis
                        dataKey="name"
                        type="category"
                        width={150}
                        stroke="#9CA3AF"
                        tick={{ fontSize: 12 }}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "#1f2937",
                          border: "none",
                          color: "#fff",
                        }}
                        formatter={(value: number) => [`${value} alertas`, 'Quantidade']}
                      />
                      <Bar dataKey="value" fill="#3B82F6" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                  * Os valores exibidos representam a quantidade de alertas gerados
                  por filial no período dos filtros da página.
                </p>
              </div>

              {/* Alerts Summary */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  Resumo de Alertas
                </h3>
                <ResponsiveContainer
                  width="100%"
                  height={300}
                  minWidth={0}
                  minHeight={0}
                >
                  <BarChart
                    data={[
                      {
                        type: "Gerados",
                        count: systemData.alerts_summary?.generated || 0,
                      },
                      {
                        type: "Resolvidos",
                        count: systemData.alerts_summary?.resolved || 0,
                      },
                    ]}
                    layout="vertical"
                    margin={{
                      top: 5,
                      right: 30,
                      left: 0,
                      bottom: 5,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" />
                    <YAxis dataKey="type" type="category" width={120} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#EF4444" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                  * Os valores exibidos representam a quantidade de alertas
                  gerados e resolvidos no período selecionado.
                </p>
              </div>

              {/* Agent Version Distribution */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  Distribuição de Versões do Agente
                </h3>
                {!systemData.agent_versions ||
                systemData.agent_versions.length === 0 ? (
                  <p className="text-gray-500 dark:text-gray-400">
                    Nenhum dado de versão do agente disponível
                  </p>
                ) : (
                  <ResponsiveContainer
                    width="100%"
                    height={400}
                    minWidth={0}
                    minHeight={0}
                  >
                    <PieChart>
                      <Pie
                        data={systemData.agent_versions.map((item: any) => ({
                          name: item.version,
                          value: item.count,
                          machines: item.machines,
                        }))}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={(props: any) => {
                          const { name, percent } = props;
                          return `${name}: ${(percent * 100).toFixed(0)}%`;
                        }}
                        outerRadius={120}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {systemData.agent_versions.map(
                          (_: any, index: number) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={COLORS[index % COLORS.length]}
                            />
                          ),
                        )}
                      </Pie>
                      <Tooltip
                        content={({ active, payload }) => {
                          if (active && payload && payload[0]) {
                            const data = payload[0].payload;
                            return (
                              <div className="bg-white dark:bg-gray-800 p-3 border border-gray-200 dark:border-gray-700 rounded shadow-lg">
                                <p className="font-semibold text-gray-900 dark:text-white">
                                  {data.name}
                                </p>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                  Quantidade: {data.value} máquina(s)
                                </p>
                                {data.machines && data.machines.length > 0 && (
                                  <div className="mt-2">
                                    <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                      Máquinas:
                                    </p>
                                    <div className="max-h-40 overflow-y-auto">
                                      {data.machines.map(
                                        (machine: any, idx: number) => (
                                          <p
                                            key={idx}
                                            className="text-xs text-gray-600 dark:text-gray-400"
                                          >
                                            • {machine.name}
                                          </p>
                                        ),
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                )}
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                  * Os valores exibidos representam a quantidade de máquinas com
                  cada versão do agente instalada. Passe o mouse sobre uma fatia
                  para ver a lista de máquinas.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Off-Hours Usage Report */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Maquinas ligadas fora do horário de trabalho
              </h2>
              {loadedReports.offHoursUsage &&
                offHoursUsageData &&
                offHoursUsageData.enabled &&
                offHoursUsageData.summary?.config && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Horário monitorado:{" "}
                    {offHoursUsageData.summary.config.start_time || "08:00"} às{" "}
                    {offHoursUsageData.summary.config.end_time || "18:00"}
                  </p>
                )}
            </div>
            <div className="flex space-x-2">
              {!loadedReports.offHoursUsage ? (
                <button
                  onClick={loadOffHoursUsageReport}
                  disabled={loadingReports.offHoursUsage}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {loadingReports.offHoursUsage ? (
                    <>
                      <LoadingSpinner size="sm" className="mr-2" />
                      Carregando...
                    </>
                  ) : (
                    "Carregar Relatório"
                  )}
                </button>
              ) : (
                <>
                  <button
                    onClick={() => exportReportToPDF("offHoursUsage")}
                    disabled={isExporting}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    <DocumentArrowDownIcon className="w-4 h-4 mr-1" />
                    PDF
                  </button>
                  <button
                    onClick={() => exportReportToImage("offHoursUsage")}
                    disabled={isExporting}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md shadow-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    <PhotoIcon className="w-4 h-4 mr-1" />
                    PNG
                  </button>
                </>
              )}
            </div>
          </div>

          {loadedReports.offHoursUsage && offHoursUsageData && (
            <div ref={offHoursUsageRef} className="space-y-6">
              {!offHoursUsageData.enabled ? (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                  <p className="text-yellow-800 dark:text-yellow-200">
                    {offHoursUsageData.message ||
                      "Alerta de uso fora do horário não está configurado ou está desativado. Configure nas Configurações da Empresa > Regras de Alerta."}
                  </p>
                </div>
              ) : (
                <>
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                      <div className="text-sm font-medium text-blue-600 dark:text-blue-400">
                        Total de Eventos
                      </div>
                      <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                        {offHoursUsageData.summary?.total_events || 0}
                      </div>
                    </div>
                    <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                      <div className="text-sm font-medium text-green-600 dark:text-green-400">
                        Máquinas Afetadas
                      </div>
                      <div className="text-2xl font-bold text-green-900 dark:text-green-100">
                        {offHoursUsageData.summary?.total_machines || 0}
                      </div>
                    </div>
                    <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4">
                      <div className="text-sm font-medium text-purple-600 dark:text-purple-400">
                        Horas Totais
                      </div>
                      <div className="text-2xl font-bold text-purple-900 dark:text-purple-100">
                        {typeof offHoursUsageData.summary?.total_hours ===
                        "number"
                          ? offHoursUsageData.summary.total_hours.toFixed(1)
                          : offHoursUsageData.summary?.total_hours || 0}
                        h
                      </div>
                    </div>
                  </div>

                  {/* Configuration Info */}
                  {offHoursUsageData.summary?.config && (
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        Configuração de Horário
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Horário permitido:{" "}
                        {offHoursUsageData.summary.config.start_time || "08:00"}{" "}
                        às{" "}
                        {offHoursUsageData.summary.config.end_time || "18:00"}
                      </p>
                    </div>
                  )}

                  {/* Usage Data Chart and Table */}
                  {offHoursUsageData.usage_data &&
                  offHoursUsageData.usage_data.length > 0 ? (
                    <>
                      {/* Machines by Days Chart */}
                      {offHoursUsageData.machines_by_days &&
                        offHoursUsageData.machines_by_days.length > 0 && (
                          <div>
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                              Dias com Ocorrências por Máquina
                            </h3>
                            <ResponsiveContainer
                              width="100%"
                              height={Math.max(
                                400,
                                offHoursUsageData.machines_by_days.length * 40,
                              )}
                              minWidth={0}
                              minHeight={0}
                            >
                              <BarChart
                                data={offHoursUsageData.machines_by_days}
                                layout="vertical"
                                margin={{
                                  top: 5,
                                  right: 30,
                                  left: 10,
                                  bottom: 5,
                                }}
                              >
                                <CartesianGrid
                                  strokeDasharray="3 3"
                                  horizontal={false}
                                />
                                <XAxis
                                  type="number"
                                  tickFormatter={(value) => `${value}d`}
                                  allowDecimals={false}
                                />
                                <YAxis
                                  dataKey="machine_name"
                                  type="category"
                                  width={180}
                                  tick={({ x, y, payload }) => {
                                    const data =
                                      offHoursUsageData.machines_by_days[
                                        payload.index
                                      ];
                                    const machineName =
                                      data?.machine_name || payload.value;
                                    const branchName = data?.branch_name || "";
                                    return (
                                      <g transform={`translate(${x},${y})`}>
                                        <text
                                          x={-5}
                                          y={-4}
                                          dy={0}
                                          textAnchor="end"
                                          className="fill-gray-700 dark:fill-gray-300 text-xs font-medium"
                                        >
                                          {machineName.length > 25
                                            ? machineName.substring(0, 25) +
                                              "..."
                                            : machineName}
                                        </text>
                                        <text
                                          x={-5}
                                          y={10}
                                          dy={0}
                                          textAnchor="end"
                                          className="fill-gray-500 dark:fill-gray-400 text-[10px]"
                                        >
                                          {branchName.length > 30
                                            ? branchName.substring(0, 30) +
                                              "..."
                                            : branchName}
                                        </text>
                                      </g>
                                    );
                                  }}
                                />
                                <Tooltip
                                  content={({ active, payload }) => {
                                    if (active && payload && payload[0]) {
                                      const data = payload[0].payload;
                                      return (
                                        <div className="bg-white dark:bg-gray-800 p-3 border border-gray-200 dark:border-gray-700 rounded shadow-lg">
                                          <p className="font-semibold text-gray-900 dark:text-white">
                                            {data.machine_name}
                                          </p>
                                          <p className="text-sm text-gray-600 dark:text-gray-400">
                                            Filial:{" "}
                                            {data.branch_name || "Sem filial"}
                                          </p>
                                          <p className="text-sm text-gray-600 dark:text-gray-400">
                                            Dias com ocorrências:{" "}
                                            {data.days_count}
                                          </p>
                                        </div>
                                      );
                                    }
                                    return null;
                                  }}
                                />
                                <Bar
                                  dataKey="days_count"
                                  fill="#3B82F6"
                                  radius={[0, 4, 4, 0]}
                                />
                              </BarChart>
                            </ResponsiveContainer>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                              * Os valores exibidos representam a quantidade de
                              dias distintos em que cada máquina registrou uso
                              fora do horário.
                            </p>
                          </div>
                        )}

                      {/* Hours Chart */}
                      <div>
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                          Horas de Uso Fora do Horário por Máquina
                        </h3>
                        <ResponsiveContainer
                          width="100%"
                          height={Math.max(
                            400,
                            offHoursUsageData.usage_data.length * 40,
                          )}
                          minWidth={0}
                          minHeight={0}
                        >
                          <BarChart
                            data={offHoursUsageData.usage_data}
                            layout="vertical"
                            margin={{
                              top: 5,
                              right: 30,
                              left: 10,
                              bottom: 5,
                            }}
                          >
                            <CartesianGrid
                              strokeDasharray="3 3"
                              horizontal={false}
                            />
                            <XAxis
                              type="number"
                              tickFormatter={(value) => `${value}h`}
                            />
                            <YAxis
                              dataKey="machine_name"
                              type="category"
                              width={180}
                              tick={({ x, y, payload }) => {
                                const data =
                                  offHoursUsageData.usage_data[payload.index];
                                const machineName =
                                  data?.machine_name || payload.value;
                                const branchName = data?.branch_name || "";
                                return (
                                  <g transform={`translate(${x},${y})`}>
                                    <text
                                      x={-5}
                                      y={-4}
                                      dy={0}
                                      textAnchor="end"
                                      className="fill-gray-700 dark:fill-gray-300 text-xs font-medium"
                                    >
                                      {machineName.length > 25
                                        ? machineName.substring(0, 25) + "..."
                                        : machineName}
                                    </text>
                                    <text
                                      x={-5}
                                      y={10}
                                      dy={0}
                                      textAnchor="end"
                                      className="fill-gray-500 dark:fill-gray-400 text-[10px]"
                                    >
                                      {branchName.length > 30
                                        ? branchName.substring(0, 30) + "..."
                                        : branchName}
                                    </text>
                                  </g>
                                );
                              }}
                            />
                            <Tooltip
                              content={({ active, payload }) => {
                                if (active && payload && payload[0]) {
                                  const data = payload[0].payload;
                                  return (
                                    <div className="bg-white dark:bg-gray-800 p-3 border border-gray-200 dark:border-gray-700 rounded shadow-lg">
                                      <p className="font-semibold text-gray-900 dark:text-white">
                                        {data.machine_name}
                                      </p>
                                      <p className="text-sm text-gray-600 dark:text-gray-400">
                                        Filial:{" "}
                                        {data.branch_name || "Sem filial"}
                                      </p>
                                      <p className="text-sm text-gray-600 dark:text-gray-400">
                                        Horas: {data.total_hours}h
                                      </p>
                                      <p className="text-sm text-gray-600 dark:text-gray-400">
                                        Eventos: {data.total_events}
                                      </p>
                                    </div>
                                  );
                                }
                                return null;
                              }}
                            />
                            <Bar
                              dataKey="total_hours"
                              fill="#EF4444"
                              radius={[0, 4, 4, 0]}
                            />
                          </BarChart>
                        </ResponsiveContainer>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                          * Os valores exibidos representam a quantidade de
                          horas que cada máquina ficou ligada fora do horário
                          permitido.
                        </p>
                      </div>

                      {/* Detailed Table */}
                      <div>
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                          Detalhamento por Máquina
                        </h3>
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-700">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                  Máquina
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                  Filial
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                  Usuário
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                  Eventos
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                  Horas
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                  Primeira Ocorrência
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                  Última Ocorrência
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                              {offHoursUsageData.usage_data.map(
                                (item: any, index: number) => (
                                  <tr key={index}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                                      {item.machine_name}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                      {item.branch_name || "Sem filial"}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                      {item.user_name}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                      {item.total_events}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                      {item.total_hours}h
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                      {item.first_occurrence
                                        ? format(
                                            new Date(item.first_occurrence),
                                            "dd/MM/yyyy HH:mm",
                                          )
                                        : "-"}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                      {item.last_occurrence
                                        ? format(
                                            new Date(item.last_occurrence),
                                            "dd/MM/yyyy HH:mm",
                                          )
                                        : "-"}
                                    </td>
                                  </tr>
                                ),
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                      <p className="text-gray-500 dark:text-gray-400">
                        Nenhum evento de uso fora do horário encontrado no
                        período selecionado.
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
